import { useQuery } from '@tanstack/react-query'
import { httpClient } from '@/api/http-client'

export type CashflowItem = {
  number: number
  value: number
  due_date: string
}

export type EducationItem = {
  type: 'graduation' | 'residency' | 'post_graduation'
  institution: string
  country: string
  status: 'completed' | 'in_progress'
  completion_date: string | null
  specialty: string | null
}

export type FinancialPerson = {
  key: string
  name: string
  tax_id?: string
  crm?: string | null
  education?: EducationItem[] | null
  credit_purpose_description?: string | null
  has_medical_company?: boolean | null
  medical_company_tax_id?: string | null
  accounting_proposal_requested?: boolean | null
  monthly_income?: string | number | null
  spouse_monthly_income?: string | number | null
  occupation_type?: string | null
  occupation_description?: string | null
  education_level?: string | null
  authorized_scr?: boolean | null
  post_graduation_plans_other?: string | null
  tuition_payment_story?: string | null
  financing_reason?: string | null
  commitment_term_accepted_at?: string | null
  email: string
  phone: string
  birthdate: string | null
  gender: string | null
  marital_status: string | null
  politically_exposed: boolean | null
  us_person: boolean | null
  spouse_name: string | null
  spouse_tax_id: string | null
  father_not_registered: boolean | null
  father_name: string | null
  father_tax_id: string | null
  father_education_level: string | null
  mother_name: string | null
  mother_tax_id: string | null
  mother_education_level: string | null
  credit_purposes: string[]
  credit_purpose_other: string | null
  monthly_family_income: string | number | null
  free_text: string | null
  high_school_type: string | null
  academic_award: boolean | null
  academic_award_description: string | null
  course_locked: boolean | null
  course_locked_reason: string | null
  post_graduation_plans: string[]
  residency_timing: string | null
  scholarships: string[]
  scholarships_other: string | null
  scholarship_percentage: string | number | null
  student_financing: string[]
  student_financing_other: string | null
  student_financing_percentage: string | number | null
  tuition_payment_sources: string[]
  monthly_tuition: string | number | null
  address: {
    zip_code: string
    street: string
    number: string
    complement: string | null
    district: string
    municipality_id: number
    municipality?: { state: string; name: string } | null
  }[]
  bank_account: {
    bank_code: string
    agency_number: string
    agency_digit: string
    account_number: string
    account_digit: string
    account_type: string
  }[]
  documents: {
    key: string
    type: string
    original_name: string
    status: string | null
  }[]
}

export type FinancialApplication = {
  key: string
  status: string
  step: string
  product: string
  denied_at?: string | null
  simulation: {
    amount: string
    installments: number
    grace_period: number
    is_combo: boolean
    cashflow: CashflowItem[]
    disbursements?: number
    start_semester?: number | null
    paid_tuitions?: number | null
    college_debt?: string | null
  } | null
  college: {
    key: string
    name: string
    college_type: 'public' | 'private'
  } | null
  application_persons: {
    person_types: string[]
    relationship?: string | null
    person: FinancialPerson
  }[]
  committee: {
    proposal: FinancialProposal | null
  } | null
}

export type FinancialProposal = {
  key: string
  amount: string
  disbursement_amount: string
  monthly_interest: string
  installments: number
  disbursements: number
  grace_period: number
  is_combo: boolean
  cashflow: CashflowItem[]
  expiration_date: string
  accepted_at: string | null
}

type RawCommittee = {
  proposal?: FinancialProposal[] | FinancialProposal | null
}

type RawApplication = Omit<FinancialApplication, 'committee'> & {
  committee?: RawCommittee[] | RawCommittee | null
}

/** O financial devolve comitês e propostas em lista, do mais recente ao mais antigo. */
export function normalizeApplication(
  raw: RawApplication,
): FinancialApplication {
  const committee = Array.isArray(raw.committee)
    ? raw.committee[0]
    : raw.committee
  const proposals = committee?.proposal
  const proposal = Array.isArray(proposals)
    ? (proposals[0] ?? null)
    : (proposals ?? null)
  return { ...raw, committee: committee ? { proposal } : null }
}

export function isProposalExpired(
  proposal: FinancialProposal,
  now = new Date(),
) {
  return (
    !proposal.accepted_at &&
    new Date(proposal.expiration_date).getTime() < now.getTime()
  )
}

export function applicationQueryKey(applicationKey: string) {
  return ['financial-application', applicationKey]
}

export function useReadApplication(applicationKey: string) {
  return useQuery({
    queryKey: applicationQueryKey(applicationKey),
    queryFn: async () => {
      const response = await httpClient('financial').get<RawApplication>(
        `/v1/application/${applicationKey}`,
      )
      return normalizeApplication(response.data)
    },
    enabled: Boolean(applicationKey),
  })
}
